import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  Checkbox, 
  FormControlLabel, 
  Typography, 
  Box, 
  Alert,
  CircularProgress,
  TextField,
  InputAdornment
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { userService } from '../services/api';
import { useUser } from '../context/UserContext';
import './SkillsSelectionPopup.css';

const SkillsSelectionPopup = ({ isOpen, onSkillsSelected, required = true }) => {
    const { user, handleSkillsCompleted } = useUser();
    const [availableSkills, setAvailableSkills] = useState([]);
    const [selectedSkills, setSelectedSkills] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    // Fetch available skills when component mounts
    useEffect(() => {
        if (isOpen) {
            fetchAvailableSkills();
        }
    }, [isOpen]);

    const fetchAvailableSkills = async () => {
        try {
            setLoading(true);
            setError('');
            const response = await userService.getAvailableSkills();
            if (response.data.success) {
                console.log(response.data.data.skills);
                setAvailableSkills(response.data.data.skills || []);
            } else {
                setError('Failed to load skills');
            }
        } catch (error) {
            console.error('Error fetching skills:', error);
            setError('Failed to load skills. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleSkillToggle = (skillId) => {
        setSelectedSkills(prev => {
            if (prev.includes(skillId)) {
                return prev.filter(id => id !== skillId);
            } else {
                return [...prev, skillId];
            }
        });
    };

    const handleSaveSkills = async () => {
        if (selectedSkills.length === 0) {
            setError('Please select at least one skill');
            return;
        }

        try {
            setSaving(true);
            setError('');        
            const response = await userService.saveUserSkills(selectedSkills);
            if (response.data.success) {
                handleSkillsCompleted();
                onSkillsSelected();
                setSelectedSkills([]);
                setSearchTerm('');
            } else {
                setError(response.data.message || 'Failed to save skills');
            }
        } catch (error) {
            console.error('Error saving skills:', error);
            setError('Failed to save skills. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    // Filter skills based on search term
    const filteredSkills = availableSkills.filter(skill =>
        skill.skill_name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    const handleClose = () => {
        if (required && !saving) {
            setError('Please select your skills to continue using the platform');
            return;
        }
        if (!saving) {
            setSelectedSkills([]);
            setSearchTerm('');
            setError('');
            onSkillsSelected();
            // Note: Don't call onSkillsSelected here as it indicates completion
        }
    };
    //if (!isOpen) return null;

    
    return (
        <Dialog 
        open={isOpen} 
        onClose={handleClose}
        maxWidth="md" 
        fullWidth
        // CHANGE: Prevent closing while saving or if no close handler
        disableEscapeKeyDown={required || saving}
        onBackdropClick={required ? undefined : handleClose}
        >
        <DialogTitle>
            <Typography variant="h5" component="h2">
                Select Your Skills
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                Please select the skills that match your expertise and interests. This will help us personalize your learning experience.
            </Typography>

            {required && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                        <Typography variant="body2">
                            <strong>Note:</strong> You must select at least one skill to continue using the platform.
                        </Typography>
                    </Alert>
            )}
        </DialogTitle>

        <DialogContent>
            {/* CHANGE: Add search functionality */}
            <Box sx={{ mb: 3 }}>
            <TextField
                fullWidth
                placeholder="Skills"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                startAdornment: (
                    <InputAdornment position="end">
                    <SearchIcon />
                    </InputAdornment>
                ),
                }}
                variant="outlined"
                size="small"
                disabled={saving}
            />
            </Box>

            {/* CHANGE: Show error message if any */}
            {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
                {error}
            </Alert>
            )}

            {/* CHANGE: Show loading state */}
            {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                <CircularProgress />
                <Typography variant="body2" sx={{ ml: 2 }}>
                    Loading skills...
                </Typography>
            </Box>
            ) : (
            <>
                {/* CHANGE: Skills selection area - simplified without categories */}
                <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
                {filteredSkills.length === 0 ? (
                    <Typography variant="body2" color="textSecondary" textAlign="center" py={4}>
                    {searchTerm ? 'No skills found matching your search.' : 'No skills available.'}
                    </Typography>
                ) : (
                    <Box>
                        {filteredSkills.map((skill) => (
                            <FormControlLabel
                            key={skill.id}
                            control={
                                <Checkbox 
                                    className ="skill-checkbox" 
                                    checked={selectedSkills.includes(skill.id)}
                                    onChange={() => handleSkillToggle(skill.id)}
                                    disabled={saving}
                                />
                            }
                            label={
                                <Box>
                                    <Typography variant="body1">
                                        {skill.skill_name}
                                    </Typography>
                                </Box>
                            }
                            sx={{ 
                                display: 'flex', 
                                alignItems: 'flex-start',
                                mb: 1,
                                '& .MuiFormControlLabel-label': {
                                    width: '100%',
                                    marginLeft: 1
                                }
                            }}
                            />
                        ))}
                    </Box>
                )}
                </Box>

                {/* CHANGE: Show selected skills count */}
                {selectedSkills.length > 0 && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
                    <Typography variant="body2" color="primary.contrastText">
                    {selectedSkills.length} skill{selectedSkills.length !== 1 ? 's' : ''} selected
                    </Typography>
                </Box>
                )}
            </>
            )}
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
            {/* CHANGE: Updated button actions */}
            <Button 
            onClick={handleClose} 
            disabled={saving}
            variant="outlined"
            >
            Cancel
            </Button>
            <Button 
            onClick={handleSaveSkills} 
            disabled={loading || saving || selectedSkills.length === 0}
            variant="contained"
            startIcon={saving ? <CircularProgress size={20} /> : null}
            >
            {saving ? 'Saving...' : `Save Skills (${selectedSkills.length})`}
            </Button>
        </DialogActions>
        </Dialog>
    );
};

export default SkillsSelectionPopup;